import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useSnackbar } from '@/hooks/global-tools';
import ChevronRightIcon from '@/public/icons/approval/chevron-right.svg';
import ArrowLeftIcon from '@/public/icons/approval/arrow-left.svg';
import CloseIcon from '@/public/icons/approval/close.svg';
import TranslationIcon from '@/public/icons/approval/translation.svg';
import useAITranslateLanguages, {
  type AITranslateLanguage,
} from '@/hooks/approval/useAITranslateLanguages';
import {
  isTranslatedAtom,
  translationFromLangAtom,
  translationResultAtom,
  translationToLangAtom,
} from '../atoms';
import {
  BagicText,
  CurrentLanguage,
  IconArrowWrap,
  LanguageButton,
  LanguageList,
  LanguageListItem,
  LanguageSelectorButton,
  MenuBox,
  MenuBoxDepth2,
  MobileBackdrop,
  MobileCloseButton,
  MobileGoTranslateButton,
  MobileMenuTitle,
  MobileMenuTitleBackButton,
  MobileMenuTitleCenterText,
  MobileMenuTitleTranslationIcon,
  RecentLanguagesLabel,
  SelectedLanguageInfo,
  TranslationMenuRoot,
  TranslationMenuWrap,
} from './styles';

/** 모바일 breakpoint — styles.tsx MOBILE_QUERY·useLayoutEffect 의 768 과 일치 유지 */
const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

/**
 * 번역 언어 선택 팝업 — depth1(From/To) + depth2(언어 리스트). 선택 언어는 jotai atom 영속.
 * 모바일(≤768px)은 풀스크린 bottom-sheet 이며 하단 "번역 시작" 버튼이 명시 액션.
 */

type TranslationMenuProps = {
  open: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  onRequestTranslation: (from: string | null, to: string | null) => void;
};

/** 자주 쓰는 언어 코드 */
const FREQUENT_LANGUAGE_CODES = ['ko', 'en', 'zh-CN'] as const;

/** From===To 동일 언어 선택 시 안내 문구 */
const SAME_LANGUAGE_MESSAGE =
  '시작 언어와 도착 언어가 같습니다.\n다른 언어를 선택해 주세요.';

const formatLanguageDisplay = (lang: AITranslateLanguage) =>
  `${lang.nativeName} (${lang.englishName})`;

/** depth2 가 클리핑되지 않도록, 가장 가까운 스크롤 ancestor 요소를 찾는다 */
const findScrollAncestor = (el: HTMLElement | null): HTMLElement | null => {
  if (typeof window === 'undefined') return null;
  let current: HTMLElement | null = el?.parentElement ?? null;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay'
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const TranslationMenu = ({
  open,
  onClose,
  anchorRef,
  onRequestTranslation,
}: TranslationMenuProps) => {
  const { t } = useTranslation();
  // SSR 첫 렌더는 false(데스크탑 마크업) — 메뉴는 open 후에만 렌더돼 실제 표시 시점 측정값은 정확.
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const depth1Ref = useRef<HTMLDivElement | null>(null);
  const depth2Ref = useRef<HTMLDivElement | null>(null);

  // 선택된 언어 — jotai atomWithLocalStorage 가 storage 동기화까지 처리.
  const [selectedFromLang, setSelectedFromLang] = useAtom(
    translationFromLangAtom,
  );
  const [selectedToLang, setSelectedToLang] = useAtom(translationToLangAtom);
  const isTranslated = useAtomValue(isTranslatedAtom);
  const setIsTranslated = useSetAtom(isTranslatedAtom);
  const setTranslationResult = useSetAtom(translationResultAtom);

  // depth2 (언어 리스트) 패널 토글 + 어떤 버튼에서 열렸는지.
  const [depth2Type, setDepth2Type] = useState<'from' | 'to' | null>(null);

  // depth2 펼침 방향 — 우측 공간 부족 시 left 전환 (레거시 adjustDepth2Position 동일).
  const [depth2AlignSide, setDepth2AlignSide] = useState<'left' | 'right'>(
    'right',
  );

  // depth2 인라인 top — 레거시 40 - depth1Rect.top 이행. viewport 기준 상단 y=40 sticky.
  const [depth2TopPx, setDepth2TopPx] = useState<number>(40);

  // 언어 목록 — 메뉴가 한 번이라도 열렸을 때만 fetch.
  const { languages: apiLanguages } = useAITranslateLanguages(open);

  // 렌더 순서: 자주 쓰는 언어 → 나머지 englishName ASC.
  const orderedLanguages = useMemo<AITranslateLanguage[]>(() => {
    if (apiLanguages.length === 0) return [];
    const frequent = FREQUENT_LANGUAGE_CODES.map((code) =>
      apiLanguages.find((l) => l.code === code),
    ).filter((l): l is AITranslateLanguage => Boolean(l));
    const frequentCodeSet = new Set(frequent.map((l) => l.code));
    const rest = apiLanguages
      .filter((l) => !frequentCodeSet.has(l.code))
      .sort((a, b) => a.englishName.localeCompare(b.englishName));
    return [...frequent, ...rest];
  }, [apiLanguages]);

  // 외부 클릭 / ESC 닫기.
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  // 메뉴 닫힐 때 depth2 도 초기화.
  useEffect(() => {
    if (!open) setDepth2Type(null);
  }, [open]);

  /**
   * 메뉴가 열릴 때 From/To 가 채워져 있고 미번역이면 곧장 번역 트리거 — 메뉴 열기 = 명시적 사용자 액션.
   * 새로고침만으로는 발동하지 않아 "끈 상태 새로고침 → 자동 번역" 버그가 재발하지 않는다.
   * ref 가드로 open 사이클당 1회만 발동.
   */
  const openTriggerFiredRef = useRef(false);
  useEffect(() => {
    if (!open) {
      openTriggerFiredRef.current = false;
      return;
    }
    if (openTriggerFiredRef.current) return;
    if (isTranslated) return;
    if (!selectedFromLang || !selectedToLang) return;
    if (selectedFromLang === selectedToLang) return;
    // 모바일은 하단 "번역 시작" 버튼이 명시 액션이므로 본 오픈 트리거 skip.
    if (isMobile) return;
    openTriggerFiredRef.current = true;
    onRequestTranslation(selectedFromLang, selectedToLang);
  }, [
    open,
    isTranslated,
    selectedFromLang,
    selectedToLang,
    onRequestTranslation,
    isMobile,
  ]);

  /**
   * depth2 위치 자동 조정(레거시 adjustDepth2Position) — 열릴 때 1회 + resize 120ms debounce.
   * 모바일은 CSS bottom-sheet 이 처리하므로 skip.
   */
  useLayoutEffect(() => {
    const depth2Open = depth2Type !== null;
    if (!depth2Open) return;

    const recompute = () => {
      if (typeof window === 'undefined') return;
      if (window.innerWidth <= 768) return;
      const depth1El = depth1Ref.current;
      const depth2El = depth2Ref.current;
      if (!depth1El || !depth2El) return;
      const depth1Rect = depth1El.getBoundingClientRect();
      const depth2Width = depth2El.offsetWidth;
      // 메뉴가 조회 모달(overflow wrapper) 안이라 viewport·모달 rect 중 제약 큰 값으로 클램핑.
      const scrollAncestor = findScrollAncestor(depth1El);
      const containerRect = scrollAncestor?.getBoundingClientRect();

      const THRESHOLD = 20;
      const rightBoundary =
        Math.min(window.innerWidth, containerRect?.right ?? window.innerWidth) -
        THRESHOLD;
      const leftBoundary = Math.max(0, containerRect?.left ?? 0) + THRESHOLD;
      const fitsRight = depth1Rect.right + 3 + depth2Width <= rightBoundary;
      const fitsLeft = depth1Rect.left - 3 - depth2Width >= leftBoundary;
      // 우측 우선 → 우측 넘치면 좌측 → 좌측마저 폭 부족이면 우측 유지(clamp 미적용).
      setDepth2AlignSide(!fitsRight && fitsLeft ? 'left' : 'right');
      const minViewportY = containerRect ? containerRect.top : 0;
      const targetViewportY = Math.max(40, minViewportY);
      setDepth2TopPx(targetViewportY - depth1Rect.top);
    };

    recompute();

    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(recompute, 120);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, [depth2Type]);

  const handleSelectorClick = useCallback((type: 'from' | 'to') => {
    setDepth2Type(type);
  }, []);

  const snackbar = useSnackbar();
  /**
   * 동일 언어(From===To) 선택 시 에러 토스트 + from/to·번역 상태 전체 초기화 + depth2 닫기.
   * depth1 은 유지 — 곧장 다시 선택 가능. 재선택 시 handleLanguagePick 트리거가 정상 재발동.
   */
  const handleSameLanguage = useCallback(() => {
    snackbar.showError({ message: t(SAME_LANGUAGE_MESSAGE) });
    setSelectedFromLang(null);
    setSelectedToLang(null);
    setIsTranslated(false);
    setTranslationResult(null);
    setDepth2Type(null);
  }, [
    snackbar,
    setSelectedFromLang,
    setSelectedToLang,
    setIsTranslated,
    setTranslationResult,
  ]);

  const handleLanguagePick = useCallback(
    (code: string) => {
      if (!depth2Type) return;
      if (depth2Type === 'from' && selectedToLang === code) {
        handleSameLanguage();
        return;
      }
      if (depth2Type === 'to' && selectedFromLang === code) {
        handleSameLanguage();
        return;
      }
      let nextFrom = selectedFromLang;
      let nextTo = selectedToLang;
      if (depth2Type === 'from') {
        nextFrom = code;
        setSelectedFromLang(code);
      } else {
        nextTo = code;
        setSelectedToLang(code);
      }
      setDepth2Type(null);
      // 데스크탑: 사용자 액션 시점에만 트리거 — atom watch 자동 트리거면 새로고침 시
      // localStorage 복원만으로 재호출됨. 모바일은 하단 "번역 시작" 버튼에서만 호출.
      if (!isMobile && nextFrom && nextTo && nextFrom !== nextTo) {
        onRequestTranslation(nextFrom, nextTo);
      }
    },
    [
      depth2Type,
      selectedFromLang,
      selectedToLang,
      handleSameLanguage,
      setSelectedFromLang,
      setSelectedToLang,
      onRequestTranslation,
      isMobile,
    ],
  );

  /** depth2 back 버튼(모바일) — depth1 복귀, 언어 선택 상태 유지 */
  const handleDepth2Back = useCallback(() => {
    setDepth2Type(null);
  }, []);

  /** 모바일 "번역 시작" — onRequestTranslation 만 호출하고 메뉴 닫음 (atom 은 이미 선택됨) */
  const handleGoTranslate = useCallback(() => {
    if (
      selectedFromLang &&
      selectedToLang &&
      selectedFromLang !== selectedToLang
    ) {
      onRequestTranslation(selectedFromLang, selectedToLang);
      onClose();
    }
  }, [selectedFromLang, selectedToLang, onRequestTranslation, onClose]);

  if (!open) return null;

  const fromLang = orderedLanguages.find((l) => l.code === selectedFromLang);
  const toLang = orderedLanguages.find((l) => l.code === selectedToLang);
  const depth2Open = depth2Type !== null;
  const canStartTranslation = Boolean(
    selectedFromLang && selectedToLang && selectedFromLang !== selectedToLang,
  );
  // 모바일은 depth1/depth2 중 하나만, 데스크탑은 둘 다(depth2 가 absolute 로 옆에 펼쳐짐).
  const showDepth1 = !isMobile || !depth2Open;
  const showDepth2 = depth2Open;

  return (
    <TranslationMenuRoot ref={rootRef}>
      {/* 모바일 딤 backdrop — 데스크탑은 styled media query 로 hidden */}
      <MobileBackdrop onClick={onClose} />
      <TranslationMenuWrap>
        {isMobile && (
          <MobileMenuTitle isBack={depth2Open}>
            {depth2Open ? (
              <MobileMenuTitleBackButton
                type="button"
                aria-label={t('뒤로')}
                onClick={handleDepth2Back}
              >
                <ArrowLeftIcon width={28} height={28} />
                <span>{t('번역하기')}</span>
              </MobileMenuTitleBackButton>
            ) : (
              <>
                <MobileMenuTitleTranslationIcon>
                  <TranslationIcon width={28} height={28} />
                </MobileMenuTitleTranslationIcon>
                <MobileMenuTitleCenterText>
                  {t('번역하기')}
                </MobileMenuTitleCenterText>
              </>
            )}
            <MobileCloseButton
              type="button"
              aria-label={t('닫기')}
              onClick={onClose}
            >
              <CloseIcon width={28} height={28} />
            </MobileCloseButton>
          </MobileMenuTitle>
        )}
        {showDepth1 && (
          <MenuBox ref={depth1Ref}>
            <LanguageSelectorButton
              type="button"
              data-type="from"
              onClick={() => handleSelectorClick('from')}
            >
              <SelectedLanguageInfo>
                <BagicText>From</BagicText>
                <CurrentLanguage selected={Boolean(fromLang)}>
                  {fromLang ? formatLanguageDisplay(fromLang) : t('선택')}
                </CurrentLanguage>
              </SelectedLanguageInfo>
              <IconArrowWrap>
                <ChevronRightIcon width={12} height={12} />
              </IconArrowWrap>
            </LanguageSelectorButton>
            <LanguageSelectorButton
              type="button"
              data-type="to"
              onClick={() => handleSelectorClick('to')}
            >
              <SelectedLanguageInfo>
                <BagicText>To</BagicText>
                <CurrentLanguage selected={Boolean(toLang)}>
                  {toLang ? formatLanguageDisplay(toLang) : t('선택')}
                </CurrentLanguage>
              </SelectedLanguageInfo>
              <IconArrowWrap>
                <ChevronRightIcon width={12} height={12} />
              </IconArrowWrap>
            </LanguageSelectorButton>
          </MenuBox>
        )}
        {showDepth2 && (
          <MenuBoxDepth2
            ref={depth2Ref}
            alignSide={depth2AlignSide}
            style={{ top: depth2TopPx }}
          >
            <RecentLanguagesLabel>{t('자주 쓰는 언어')}</RecentLanguagesLabel>
            <LanguageList>
              {orderedLanguages.map((lang) => {
                const active =
                  depth2Type === 'from'
                    ? selectedFromLang === lang.code
                    : selectedToLang === lang.code;
                return (
                  <LanguageListItem key={lang.code}>
                    <LanguageButton
                      type="button"
                      active={active}
                      data-lang={lang.code}
                      onClick={() => handleLanguagePick(lang.code)}
                    >
                      {formatLanguageDisplay(lang)}
                    </LanguageButton>
                  </LanguageListItem>
                );
              })}
            </LanguageList>
          </MenuBoxDepth2>
        )}
        {isMobile && !depth2Open && (
          <MobileGoTranslateButton
            type="button"
            disabled={!canStartTranslation}
            onClick={handleGoTranslate}
          >
            {t('번역 시작')}
          </MobileGoTranslateButton>
        )}
      </TranslationMenuWrap>
    </TranslationMenuRoot>
  );
};

export default TranslationMenu;
